// Parametros de planta editables desde la consola de UCORP (AT-12, regla 10:
// todo parametro configurable tiene CRUD con auditoria). Scope /admin: solo
// ucorp_admin escribe; ucorp_admin_reader lee.
//
// Los rangos de los requests replican SITE_PARAMETER_RANGES de @ucorp/domain
// (contracts es standalone y no importa domain); apps/api verifica en un test
// que coincidan. Las respuestas NO llevan rango: muestran lo que hay en la
// base, aunque haya entrado por un manifiesto viejo con otra regla.
import { z } from "zod";
import { Instant, Integer, ProviderId, RecordId, SiteId, SiteParams, VehicleType } from "./common.js";
export const CabSharing = z.enum(["solo", "shared"]).openapi("CabSharing", {
    description: "Si la persona puede compartir el auto. solo: viaja siempre sola. shared: puede ir con otras shared de la misma planta, sentido y horario compatible (dentro del colchon de llegada).",
});
export const OnDeadlineMode = z.enum(["reoffer", "hold"]).openapi("OnDeadlineMode", {
    description: "Que pasa al vencer un plazo de la oferta: reoffer pasa el viaje solo al siguiente proveedor de la cola; hold lo deja con la cola agotada y alerta para que decida la planta.",
});
export const DefaultMobilityMode = z.enum(["shuttle", "cab"]).openapi("DefaultMobilityMode", {
    description: "Modalidad por defecto de la planta: lo que toma un legajo sin politica de movilidad valida, en la app del pasajero y en el armado. shuttle: combi con el proveedor por defecto. cab: auto. El colectivo no se ofrece porque necesita una linea por legajo.",
});
export const DocumentTypeName = z.string().trim().min(1).max(64).openapi("DocumentTypeName", {
    description: "Tipo de documento que la planta exige vigente (catalogo abierto: la planta nombra los suyos).",
    example: "seguro",
});
export const RequiredDocuments = z
    .object({
    vehicle: DocumentTypeName.array().max(20).openapi({ description: "Tipos exigidos a cada unidad, sin repetir." }),
    driver: DocumentTypeName.array().max(20).openapi({ description: "Tipos exigidos a cada chofer, sin repetir." }),
})
    .openapi("RequiredDocuments", {
    description: "Documentos que la planta exige ver vigentes, por sujeto. Listas vacias: no exige nada.",
});
export const ProviderQueueEntry = z
    .object({
    providerId: ProviderId,
    active: z.boolean().openapi({ description: "false: queda en la cola pero no se le ofrece (pausa sin perder el orden)." }),
})
    .openapi("ProviderQueueEntry", { description: "Un proveedor en la cola, en su posicion." });
export const ProviderQueue = z
    .object({
    vehicleType: VehicleType,
    providers: ProviderQueueEntry.array().openapi({ description: "En el orden en que se ofrece el viaje; el primero es el primero." }),
})
    .openapi("ProviderQueue", {
    description: "Cola de proveedores de un tipo de servicio. Sin cola para un tipo, a cada viaje solo se lo ofrece el proveedor que eligio el armado.",
});
export const SiteParameterDefaults = z
    .object({
    maxJourneyMin: Integer,
    shuttleRequestLeadTimeHours: Integer,
    cabRequestLeadTimeHours: Integer,
    arrivalBufferMin: Integer,
    handoverMarginMin: Integer,
    pickerStepMinutes: Integer,
    cabMaxRiders: Integer,
    cabSharingDefault: CabSharing,
    defaultMobilityMode: DefaultMobilityMode,
    shiftMatchWindowMin: Integer,
    cabDispatchCloseMin: Integer,
    noShowToleranceMin: Integer,
    autoDispatchEnabled: z.boolean(),
    cabPointToPointEnabled: z.boolean(),
    cabAcceptDeadlineSeconds: Integer,
    cabAssignDeadlineSeconds: Integer,
    shuttleAcceptDeadlineSeconds: Integer,
    shuttleAssignDeadlineSeconds: Integer,
    onDeadlineMode: OnDeadlineMode,
})
    .openapi("SiteParameterDefaults", {
    description: "Valores por defecto de la plataforma para cada parametro con rango. La consola los ofrece para restaurar.",
});
export const SiteParametersResponse = z
    .object({
    siteId: SiteId,
    maxJourneyMin: Integer.openapi({ description: "Tope de trayecto a bordo por pasajero, en minutos. Regla dura del armado." }),
    shuttleRequestLeadTimeHours: Integer.openapi({
        description: "Antelacion de la combi, en horas, contada desde la hora en que pasan a buscar al pasajero (llegar antes de X: X menos el tope de trayecto).",
    }),
    cabRequestLeadTimeHours: Integer.openapi({ description: "Antelacion del auto, en horas, contada desde la busqueda del pasajero." }),
    arrivalBufferMin: Integer.openapi({ description: "Colchon de llegada a porteria antes del ingreso, en minutos." }),
    handoverMarginMin: Integer.openapi({ description: "Margen de relevo: tolerancia de puntualidad, en minutos." }),
    pickerStepMinutes: Integer.openapi({ description: "Paso del selector de horarios de la app, en minutos." }),
    cabMaxRiders: Integer.openapi({ description: "Maximo de pasajeros por auto compartido. 1: nadie comparte." }),
    cabSharingDefault: CabSharing.openapi({ description: "Lo que toma un legajo sin configurar (cabSharing null)." }),
    defaultMobilityMode: DefaultMobilityMode,
    shiftMatchWindowMin: Integer.openapi({
        description: "Ventana de encaje en turno, en minutos. Un pedido de combi cuya hora queda mas lejos que esto del turno en que encaja se arma igual y queda marcado fuera de turno.",
    }),
    cabDispatchCloseMin: Integer.openapi({
        description: "Anticipacion del cierre de autos, en minutos: el despacho de un auto programado cierra a la busqueda menos este valor. Un pedido que llega con el cierre ya pasado se despacha al instante, sin consolidar.",
    }),
    noShowToleranceMin: Integer.openapi({
        description: "Tolerancia de no-show, en minutos (0.8.0, ADR-029): cuanto espera el vehiculo en la parada, contado desde la llegada o la hora planificada (la mas tardia), antes de que el conductor pueda marcar 'No se presento'. No es la tolerancia de cobro de la espera: esa es del tarifario.",
    }),
    autoDispatchEnabled: z.boolean().openapi({
        description: "Despacho automatico de la planta. false: nada se arma solo; el armado manual de la torre anda igual.",
    }),
    cabPointToPointEnabled: z.boolean().openapi({
        description: "Traslado libre en auto (origen y destino sin la planta) para toda politica con auto. false: la app no lo ofrece y el alta lo rechaza con service_not_available; el auto desde o hacia la planta sigue igual.",
    }),
    vehicleMaxAgeYears: Integer.nullable().openapi({ description: "Antiguedad maxima del vehiculo en anios. null: sin condicion." }),
    defaultShuttleProvider: ProviderId.nullable().openapi({ description: "Proveedor de combis por defecto del reparto. null: no declarado." }),
    requiredDocuments: RequiredDocuments,
    cabAcceptDeadlineSeconds: Integer.openapi({
        description: "Plazo para aceptar una oferta de un viaje en auto, en segundos. Se sella en cada oferta.",
    }),
    cabAssignDeadlineSeconds: Integer.openapi({
        description: "Plazo para cargar vehiculo y conductor de un viaje en auto despues de aceptar, en segundos.",
    }),
    shuttleAcceptDeadlineSeconds: Integer.openapi({
        description: "Plazo para aceptar una oferta de un viaje en combi o colectivo, en segundos. Se sella en cada oferta.",
    }),
    shuttleAssignDeadlineSeconds: Integer.openapi({
        description: "Plazo para cargar vehiculo y conductor de un viaje en combi o colectivo despues de aceptar, en segundos.",
    }),
    onDeadlineMode: OnDeadlineMode,
    providerQueues: ProviderQueue.array().openapi({ description: "Colas configuradas, una por tipo de servicio que tenga cola." }),
    defaults: SiteParameterDefaults,
    updatedAt: Instant.openapi({ description: "Ultima modificacion de la fila de parametros." }),
})
    .openapi("SiteParametersResponse", {
    description: "Parametros vigentes de la planta, tal como los leen el armado, el cierre y la asignacion en su proxima corrida (sin cache).",
});
// Rangos: SITE_PARAMETER_RANGES de @ucorp/domain (test de paridad en apps/api).
// strict: un campo fuera de la lista (el area de operacion, por ejemplo) es 400.
// La regla "al menos un campo" la aplica la ruta.
export const UpdateSiteParametersRequest = z
    .object({
    maxJourneyMin: Integer.min(15).max(180).optional(),
    shuttleRequestLeadTimeHours: Integer.min(12).max(72).optional(),
    cabRequestLeadTimeHours: Integer.min(0).max(72).optional().openapi({ description: "0: sin antelacion, la app puede ofrecer 'Ahora' para el auto." }),
    arrivalBufferMin: Integer.min(0).max(60).optional(),
    handoverMarginMin: Integer.min(0).max(60).optional(),
    pickerStepMinutes: Integer.min(5).max(60).optional(),
    cabMaxRiders: Integer.min(1).max(4).optional(),
    cabSharingDefault: CabSharing.optional(),
    defaultMobilityMode: DefaultMobilityMode.optional().openapi({
        description: "cab solo se acepta si la planta tiene un proveedor activo que presta auto (si no, 400).",
    }),
    shiftMatchWindowMin: Integer.min(0).max(240).optional(),
    cabDispatchCloseMin: Integer.min(15).max(240).optional(),
    noShowToleranceMin: Integer.min(1).max(30).optional(),
    autoDispatchEnabled: z.boolean().optional(),
    cabPointToPointEnabled: z.boolean().optional(),
    vehicleMaxAgeYears: Integer.min(1).max(40).nullable().optional().openapi({ description: "null borra la condicion de antiguedad." }),
    defaultShuttleProvider: ProviderId.nullable().openapi({
        description: "Proveedor de la planta que presta combi. null lo borra; solo se acepta si la planta tiene a lo sumo un proveedor de combi.",
    }).optional(),
    requiredDocuments: RequiredDocuments.optional().openapi({ description: "Reemplaza las dos listas completas." }),
    // Un par por servicio (PP-67): cambiar los de combi no toca los de auto.
    cabAcceptDeadlineSeconds: Integer.min(30).max(86_400).optional(),
    cabAssignDeadlineSeconds: Integer.min(30).max(86_400).optional(),
    shuttleAcceptDeadlineSeconds: Integer.min(30).max(86_400).optional().openapi({ description: "Rige tambien para el colectivo." }),
    shuttleAssignDeadlineSeconds: Integer.min(30).max(86_400).optional().openapi({ description: "Rige tambien para el colectivo." }),
    onDeadlineMode: OnDeadlineMode.optional(),
})
    .strict()
    .openapi("UpdateSiteParametersRequest", {
    description: "Edicion parcial de los parametros de planta. Ausente: no cambia. Fuera de rango: 400. Queda auditada con antes y despues y rige en la proxima corrida del armado y la proxima oferta; no mueve los relojes de ofertas vivas.",
});
export const ProviderQueueParams = SiteParams.extend({ vehicleType: VehicleType }).openapi("ProviderQueueParams", {
    description: "Planta y tipo de servicio de la cola.",
});
export const UpdateProviderQueueRequest = z
    .object({
    providers: ProviderQueueEntry.array().max(20).openapi({
        description: "Cola completa en orden. Sin repetir y solo proveedores de la planta que prestan ese servicio (si no, 400). Lista vacia: la planta deja de tener cola para ese tipo.",
    }),
})
    .strict()
    .openapi("UpdateProviderQueueRequest", {
    description: "Reemplaza el orden de la cola de un tipo de servicio. Queda auditado con antes y despues; rige desde la proxima primera oferta.",
});
// Auto compartido por legajo (docs/PLAN-ARMADO-TIPO-SERVICIO.md). La consola
// edita un legajo por su codigo, sin listar el padron: la respuesta no lleva
// nombre, telefono ni domicilio.
export const RecordCabSharingParams = SiteParams.extend({ record: RecordId }).openapi("RecordCabSharingParams", {
    description: "Planta y legajo.",
});
export const RecordCabSharingResponse = z
    .object({
    siteId: SiteId,
    record: RecordId,
    cabSharing: CabSharing.nullable().openapi({ description: "Lo configurado en el legajo. null: hereda el de la planta." }),
    effective: CabSharing.openapi({ description: "Lo que usa el proximo armado: el del legajo o, si es null, el de la planta." }),
})
    .openapi("RecordCabSharingResponse", { description: "Auto compartido de un legajo, sin datos personales." });
export const UpdateRecordCabSharingRequest = z
    .object({
    cabSharing: CabSharing.nullable().openapi({ description: "solo, shared o null para heredar el de la planta." }),
})
    .strict()
    .openapi("UpdateRecordCabSharingRequest", {
    description: "Cambia el auto compartido de un legajo. Auditado con antes y despues; rige en el proximo armado.",
});
//# sourceMappingURL=site-parameters.js.map